/**
 * O painel que abre ao clicar num componente do desenho.
 *
 * É a reprodução do `GraficoResultadoModal` do Sistema de hoje, com duas
 * diferenças deliberadas.
 *
 * **Painel, não modal.** O legado abre um modal centralizado, que tapa o
 * desenho. Mas navegar por componente é justamente comparar o que está escrito
 * na janela com o que está no desenho — e o modal cobre exatamente o que se
 * quer olhar. Aqui é gaveta à direita, como manda a regra 2 da padronização, e
 * o desenho continua à vista.
 *
 * **Três ações, não seis.** O legado oferece seis: gerar a partir do
 * componente, editar pinout, editar componente, e — por conector — editar
 * conector, fotos e SVG. As três primeiras existem no front novo e estão aqui.
 * As três últimas dependem de rotas de desenho e foto de conector que **a API
 * ainda não tem**; o painel diz isso em vez de mostrar botão morto.
 *
 * **Nenhuma delas sai da tela.** Editar abre aqui mesmo, na gaveta, porque o
 * desenho custa até 18 segundos e não é reaproveitado: navegar para trocar uma
 * descrição e voltar paga esses 18 segundos de novo, além de perder de vista o
 * que se estava conferindo.
 */

import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import type { ComponenteResponse, PinoutResponse } from "@/api/tipos"
import type { AlvoDeEdicao } from "./GavetaDeCadastro"

export type AlvoDoPainel = {
  /** Pinout clicado. 0 quando o clique foi no componente principal. */
  pinoutId: number
  /** Componente já conhecido — o caso do principal, que não passa por pinout. */
  componenteId: number
  principal: boolean
}

export function PainelDoComponente({
  alvo, aoFechar, aoGerarAPartirDe, aoEditarCadastro, aoEditarPinout,
}: {
  alvo: AlvoDoPainel
  aoFechar: () => void
  /** "Gerar o pinout a partir desse componente" — a navegação do legado. */
  aoGerarAPartirDe: (componenteId: number) => void
  /** Abre o cadastro na própria gaveta, sem trocar de tela. */
  aoEditarCadastro: (alvo: AlvoDeEdicao, contexto?: string) => void
  /** Abre o lançamento sobre o desenho, também sem trocar de tela. */
  aoEditarPinout: (pinoutId: number) => void
}) {

  // O desenho carrega o pinout_id no elemento; o componente vem dele. É a mesma
  // corrente do legado, que também parte do pinout para achar o componente.
  const pinout = useQuery({
    queryKey: ["pinout", alvo.pinoutId],
    enabled: alvo.pinoutId > 0,
    queryFn: () => obter<PinoutResponse>(`api/pinouts/${alvo.pinoutId}`, {}),
  })

  const componenteId = alvo.componenteId || pinout.data?.componenteId || 0

  const componente = useQuery({
    queryKey: ["componente", componenteId],
    enabled: componenteId > 0,
    queryFn: () => obter<ComponenteResponse>(`api/componentes/${componenteId}`, {}),
  })

  const carregando = pinout.isPending && alvo.pinoutId > 0
  const c = componente.data

  const titulo = c
    ? [c.codigo, c.descricao].filter((x) => x && x.trim()).filter(
        (x, i, a) => i === 0 || x.toLowerCase() !== a[0]!.toLowerCase()).join(" · ")
    : carregando || componente.isPending ? "Carregando…" : "Componente"

  return (
    <aside
      className="gaveta"
      aria-label="Componente do desenho"
      style={{
        flex: "0 0 320px", width: 320, borderLeft: "1px solid var(--line2)",
        background: "var(--surface)", display: "flex", flexDirection: "column", minHeight: 0,
      }}
    >
      <div style={{
        flex: "0 0 auto", display: "flex", alignItems: "flex-start", gap: 8,
        padding: "12px 14px", borderBottom: "1px solid var(--line)",
      }}>
        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".03em", color: "var(--txt3)" }}>
            {alvo.principal ? "COMPONENTE DO DESENHO" : "COMPONENTE LIGADO"}
          </span>
          <span className="mono" style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
            {titulo}
          </span>
        </span>
        <button onClick={aoFechar} aria-label="Fechar o painel"
                style={{ display: "flex", color: "var(--txt3)", padding: 4 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 14 }}>
        {c?.localizacao?.trim() && (
          <Bloco rotulo="Localização">{c.localizacao}</Bloco>
        )}

        {pinout.data && (
          <Bloco rotulo="Pinout clicado">
            <span className="mono">
              {pinout.data.conectorDescricao || "sem conector"} · {pinout.data.pino}
            </span>
            {pinout.data.circuitoDescricao && (
              <span style={{ color: "var(--txt3)" }}> · {pinout.data.circuitoDescricao}</span>
            )}
          </Bloco>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {/* A navegação: o `Apartir()` do legado. Só faz sentido em outro
              componente — no principal, redesenharia o mesmo desenho. */}
          {!alvo.principal && componenteId > 0 && (
            <Acao
              principal
              rotulo="Gerar o pinout a partir deste componente"
              ajuda="Redesenha centrado nele. É como se anda pelo circuito."
              aoAgir={() => aoGerarAPartirDe(componenteId)}
            />
          )}

          {alvo.pinoutId > 0 && (
            <Acao
              rotulo="Editar este pinout"
              ajuda="Abre aqui em cima, preenchido."
              aoAgir={() => aoEditarPinout(alvo.pinoutId)}
            />
          )}

          {componenteId > 0 && (
            <Acao
              rotulo="Editar o componente"
              ajuda="Abre aqui, sem sair do desenho."
              aoAgir={() => aoEditarCadastro({ entidade: "componentes", id: componenteId })}
            />
          )}

          {pinout.data?.conectorId ? (
            <Acao
              rotulo={`Editar o conector ${pinout.data.conectorDescricao}`}
              ajuda="Abre aqui, sem sair do desenho."
              aoAgir={() => aoEditarCadastro(
                { entidade: "conectores", id: pinout.data!.conectorId },
                titulo !== "Componente" ? `Componente ${titulo}` : undefined)}
            />
          ) : null}
        </div>

        {/* Honestidade sobre o que falta: o legado tem mais três ações aqui, e
            elas não existem porque a API não tem as rotas — não porque a tela
            esqueceu. Melhor dizer do que deixar a pessoa procurando. */}
        <p style={{
          margin: 0, padding: "9px 11px", borderRadius: 8, fontSize: 11,
          lineHeight: 1.5, background: "var(--surface2)", color: "var(--txt3)",
        }}>
          O Sistema antigo oferece aqui também o desenho e as fotos do conector. Ainda não estão
          nesta tela: a API não tem rota para nenhum dos dois.
        </p>
      </div>
    </aside>
  )
}

function Bloco({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".03em", color: "var(--txt3)" }}>
        {rotulo.toUpperCase()}
      </span>
      <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>{children}</span>
    </div>
  )
}

function Acao({ rotulo, ajuda, aoAgir, principal }: {
  rotulo: string; ajuda: string; aoAgir: () => void; principal?: boolean
}) {
  return (
    <button
      onClick={aoAgir}
      style={{
        display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
        width: "100%", padding: "9px 11px", borderRadius: 8, textAlign: "left",
        background: principal ? "var(--brand)" : "var(--surface2)",
        color: principal ? "#FFFFFF" : "var(--txt)",
        border: principal ? undefined : "1px solid var(--line2)",
      }}
    >
      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{rotulo}</span>
      <span style={{ fontSize: 11, opacity: principal ? 0.85 : 0.7 }}>{ajuda}</span>
    </button>
  )
}
